import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSocket } from './SocketContext.jsx';

const ConfigContext = createContext();

export const useConfig = () => useContext(ConfigContext);

// Configuration de styles par défaut
const DEFAULT_STYLES = {
  global: {
    primaryColor: '#2196F3',
    secondaryColor: '#4CAF50',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f5f5f5'
  },
  admin: {
    primaryColor: '',
    secondaryColor: '',
    fontFamily: '',
    backgroundColor: '#ffffff',
    headerColor: '#1976D2',
    borderRadius: '8px'
  },
  counter: {
    primaryColor: '',
    secondaryColor: '',
    fontFamily: '',
    backgroundColor: '#f5f5f5',
    cardColor: '#ffffff',
    ticketNumberSize: '3rem'
  },
  display: {
    primaryColor: '',
    secondaryColor: '',
    fontFamily: '',
    backgroundColor: '#2196F3',
    ticketColor: '#ffffff',
    counterSize: '2.5rem'
  },
  distributor: {
    primaryColor: '',
    secondaryColor: '',
    fontFamily: '',
    backgroundColor: '#2196F3',
    serviceButtonColor: '#ffffff',
    serviceButtonRadius: '0.5rem'
  }
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { socket } = useSocket();

  // Fonction pour initialiser/normaliser la configuration
  const normalizeConfig = (config) => {
    // S'assurer que tous les champs nécessaires existent
    const normalized = { ...config };
    
    // Vérifier et initialiser les styles si nécessaire
    if (!normalized.styles) {
      normalized.styles = DEFAULT_STYLES;
    } else {
      // S'assurer que tous les sous-objets de styles existent
      for (const panel of ['global', 'admin', 'counter', 'display', 'distributor']) {
        if (!normalized.styles[panel]) {
          normalized.styles[panel] = DEFAULT_STYLES[panel];
        }
      }
    }
    
    // Initialiser savedThemes si nécessaire
    if (!normalized.savedThemes) {
      normalized.savedThemes = [];
    }
    
    return normalized;
  };

  // Charger la configuration au démarrage
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/config');
        
        if (response.data.success) {
          // Normaliser la configuration
          const normalizedConfig = normalizeConfig(response.data.data);
          setConfig(normalizedConfig);
          setError(null);
        } else {
          setError('Erreur lors du chargement de la configuration');
          toast.error('Erreur lors du chargement de la configuration');
        }
      } catch (err) {
        console.error('Erreur lors de la récupération de la configuration:', err);
        setError('Erreur de connexion au serveur');
        toast.error('Erreur de connexion au serveur');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Écouter les mises à jour de configuration via Socket.io
  useEffect(() => {
    if (!socket) return;

    const handleConfigUpdate = (updatedConfig) => {
      // Normaliser la configuration mise à jour
      const normalizedConfig = normalizeConfig(updatedConfig);
      setConfig(normalizedConfig);
      toast.info('La configuration a été mise à jour');
    };

    socket.on('configUpdated', handleConfigUpdate);

    return () => {
      socket.off('configUpdated', handleConfigUpdate);
    };
  }, [socket]);

  // Fonction pour mettre à jour la configuration
  const updateConfig = async (pathOrNewConfig, value) => {
    try {
      setLoading(true);
      
      let updatedConfig;
      
      // Si pathOrNewConfig est un objet, il s'agit d'une mise à jour complète
      if (typeof pathOrNewConfig === 'object') {
        updatedConfig = { ...normalizeConfig(pathOrNewConfig) };
      } 
      // Sinon, c'est un chemin à mettre à jour
      else {
        // Créer une copie profonde de la configuration existante
        updatedConfig = JSON.parse(JSON.stringify(config));
        
        // Fonction helper pour mettre à jour une propriété imbriquée
        const setNestedProperty = (obj, path, value) => {
          const parts = path.split('.');
          let current = obj;
          
          for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i];
            // Si la propriété n'existe pas, la créer
            if (!current[part]) {
              current[part] = {};
            }
            current = current[part];
          }
          
          // Mettre à jour la valeur
          current[parts[parts.length - 1]] = value;
        };
        
        setNestedProperty(updatedConfig, pathOrNewConfig, value);
      }
      
      // Mettre à jour la configuration locale sans attendre la réponse du serveur
      setConfig(updatedConfig);
      
      // Envoyer la mise à jour au serveur
      const response = await axios.put('/api/config', updatedConfig);
      
      if (response.data.success) {
        // Mettre à jour avec la réponse du serveur (qui pourrait inclure des champs supplémentaires)
        setConfig(normalizeConfig(response.data.data));
        return true;
      } else {
        toast.error('Erreur lors de la mise à jour de la configuration');
        return false;
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la configuration:', err);
      toast.error('Erreur de connexion au serveur');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour restaurer une configuration précédente
  const restoreConfig = async (configId) => {
    try {
      setLoading(true);
      const response = await axios.post(`/api/config/restore/${configId}`);
      
      if (response.data.success) {
        setConfig(normalizeConfig(response.data.data));
        toast.success('Configuration restaurée avec succès');
        return true;
      } else {
        toast.error('Erreur lors de la restauration de la configuration');
        return false;
      }
    } catch (err) {
      console.error('Erreur lors de la restauration de la configuration:', err);
      toast.error('Erreur de connexion au serveur');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigContext.Provider
      value={{
        config,
        loading,
        error,
        updateConfig,
        restoreConfig
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};